import DeleteIcon from "@mui/icons-material/Delete";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import { Box, Button, Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useContext } from "react";

import { queryClient } from "App";
import { Filter } from "components/FilterPaginated";
import { Spinner } from "components/Spinner";
import { Column, Table } from "components/Table";
import { ModalContext } from "contexts/ModalProvider";
import { useAllCompanies } from "hooks/useAllCompanies";
import { useAuthentication } from "hooks/useAuthentication";
import { useLayout } from "hooks/useLayout";
import { useToast } from "hooks/useToast";
import { UserCreationModal } from "modals/UserCreationModal";
import { User } from "types/models";
import { API } from "utils/api";
import {
  humanizeRole,
  isAdmin,
  ROLES,
  COMPANY_ADMIN,
  BLUECAR_ROLES_IDS,
  isBluecarRole,
  ADMIN,
  Role,
} from "utils/models/user";
import { mapToSelect } from "utils/select";

const baseColumns: Column<User>[] = [
  { id: "name", label: "Nombre" },
  { id: "email", label: "Email" },
  { id: "phone", label: "Teléfono" },
  {
    id: "role",
    label: "Rol",
    renderer: (user) => humanizeRole(user),
  },
];

const companyColumn: Column<User> = {
  id: "company",
  label: "Empresa",
  renderer: (user) => user.company?.name,
};

const activeColumn: Column<User> = {
  id: "active",
  label: "Activo",
  renderer: (user) => (Boolean(user.active) ? "Sí" : "No"),
};

const ADMIN_ROLE: Role[] = [ADMIN];
const allFilters = [
  { id: "search", label: "Nombre / Email", type: "text" },
  {
    id: "role",
    label: "Rol",
    type: "select",
    options: ROLES,
    roles: BLUECAR_ROLES_IDS,
    multiple: true,
  },
  {
    id: "company_id",
    label: "Empresa",
    type: "select",
    roles: BLUECAR_ROLES_IDS,
    options: [],
    multiple: true,
  },
  {
    id: "active",
    label: "Activo",
    type: "select",
    roles: ADMIN_ROLE,
    multiple: false,
    options: [
      { id: "true", label: "Activo" },
      { id: "false", label: "Inactivo" },
    ],
  },
] as const;

const deleteMutation = (userId: string) => API.delete(`/users/${userId}`);
const activateMutation = ({
  userId,
  evergreen,
}: {
  userId: string;
  evergreen?: string;
}) => API.put(`/users/${userId}/activate`, { evergreen });

export const BackofficeUsers = () => {
  const { BackofficeLayout } = useLayout({
    isPrivate: true,
    title: "Usuarios",
    roles: [...BLUECAR_ROLES_IDS, COMPANY_ADMIN],
  });
  const { setConfirmationModalProps } = useContext(ModalContext);
  const { infoToast, errorToast } = useToast();
  const { user } = useAuthentication();
  const { data: companies } = useAllCompanies();

  const { mutateAsync: deleteUser } = useMutation({
    mutationFn: deleteMutation,
    onSuccess: () => {
      infoToast("Usuario eliminado con éxito.");
      queryClient.invalidateQueries(["users"]);
      setConfirmationModalProps({ open: false });
    },
    onError: () =>
      errorToast("No se ha podido eliminar el usuario, intenta nuevamente"),
  });

  const { mutateAsync: activateUser } = useMutation({
    mutationFn: activateMutation,
    onSuccess: () => {
      infoToast("Usuario reactivado con éxito.");
      queryClient.invalidateQueries(["users"]);
      setConfirmationModalProps({ open: false });
    },
    onError: () =>
      errorToast("No se ha podido reactivar el usuario, intenta nuevamente"),
  });

  const actionColumn: Column<User> = useMemo(
    () => ({
      id: "id",
      label: "Acciones",
      renderer: (rowUser) => (
        <Box display="flex" justifyContent="end" gap="5px">
          {rowUser.active ? (
            <Tooltip title="Eliminar usuario">
              <Button
                onClick={() =>
                  setConfirmationModalProps({
                    open: true,
                    title: "Eliminar usuario",
                    text: "Si eliminas el usuario perderá el acceso a su cuenta",
                    buttonText: "Eliminar",
                    buttonColor: "error",
                    onSubmit: () => deleteUser(rowUser.id),
                  })
                }
              >
                <DeleteIcon titleAccess="Eliminar" />
              </Button>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Reactivar por 90 días">
                <Button
                  color="success"
                  onClick={() =>
                    setConfirmationModalProps({
                      open: true,
                      title: "Reactivar usuario por 90 días",
                      text: "¿Seguro que deseas reactivar este usuario por 90 días?",
                      buttonText: "Reactivar",
                      buttonColor: "primary",
                      onSubmit: () => activateUser({ userId: rowUser.id }),
                    })
                  }
                >
                  <MoreTimeIcon
                    color="primary"
                    titleAccess="Reactivar por 90 días"
                  />
                </Button>
              </Tooltip>
              <Tooltip title="Reactivar por siempre">
                <Button
                  color="success"
                  onClick={() =>
                    setConfirmationModalProps({
                      open: true,
                      title: "Reactivar usuario por siempre",
                      text: "¿Seguro que deseas reactivar este usuario por siempre?",
                      buttonText: "Reactivar",
                      buttonColor: "primary",
                      onSubmit: () =>
                        activateUser({ userId: rowUser.id, evergreen: "true" }),
                      // Backend expects "true"
                    })
                  }
                >
                  <PermContactCalendarIcon
                    color="primary"
                    titleAccess="Reactivar por siempre"
                  />
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      ),
      align: "center",
    }),
    [setConfirmationModalProps, deleteUser, activateUser]
  );

  const columns = useMemo(() => {
    if (user) {
      if (isAdmin(user)) {
        return [...baseColumns, companyColumn, activeColumn, actionColumn];
      } else if (isBluecarRole(user)) {
        return [...baseColumns, companyColumn];
      }
    }
    return baseColumns;
  }, [user, actionColumn]);

  const filters: Filter[] = useMemo(() => {
    return allFilters.map((filter) => {
      if (filter.id === "company_id") {
        return {
          ...filter,
          options: mapToSelect({ elements: companies }),
        };
      }
      return filter;
    });
  }, [companies]);

  const anyFilterLoading = useMemo(() => {
    return filters.some(
      (filter) =>
        !!filter.options &&
        !filter.options.length &&
        (!filter.roles || (!!user && filter.roles.includes(user.role)))
    );
  }, [filters, user]);

  return (
    <BackofficeLayout>
      {anyFilterLoading ? (
        <Spinner />
      ) : (
        <Table
          columns={columns}
          filters={filters}
          queryKey="users"
          path="users"
        />
      )}
      {!!user && isBluecarRole(user) && <UserCreationModal user={user} />}
    </BackofficeLayout>
  );
};
